import { useLocalStorage, useTracking } from '@components/hooks';
import styles from '@components/components/Modal/TimedModal/TimedModal.module.scss';
import { usePageData } from '@components/context';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Content,
  Heading,
  Image,
  Link,
  Modal,
} from '@wienenergiegithub/ui-next';
import { useDialogStore } from '@ariakit/react';
import classNames from 'classnames';
import { useCookieConsent } from '@wienenergiegithub/modules-next/components';
import useHash from '@components/hooks/useHash';

const STATES = {
  CLOSED: 'CLOSED',
};

// In case there are two Modals valid for one page
const randomModal = (array = []) =>
  array[Math.floor(Math.random() * array.length)];

function TimedModal() {
  const { timedModals } = usePageData();
  const [hash] = useHash();

  const validModalsToShow = useMemo(() => {
    return timedModals.filter(modal => {
      // we check this first because this is the case for most modals
      if (!modal.isActive) {
        return false;
      }
      const isPathInPageList = modal.modalPageList.includes(
        window.location.pathname,
      );
      //  current page is blacklisted
      if (modal.exceptionRule === 'blackList' && isPathInPageList) {
        return false;
      }
      // current page is not whitelisted
      if (modal.exceptionRule === 'whiteList' && !isPathInPageList) {
        return false;
      }
      const isStateClosed =
        JSON.parse(localStorage.getItem(modal.storageId)) === STATES.CLOSED;
      // modal was already closed before
      if (isStateClosed) {
        return false;
      }
      if (modal?.piwikId && hash?.substring(1) !== modal.piwikId) {
        return false;
      }
      return true;
    });
  }, [timedModals, window.location.pathname, hash]);

  const cookieConsent = useCookieConsent();
  const [open, setOpen] = useState(false);
  const dialog = useDialogStore({ open, setOpen });
  const hasCookieConsent = useMemo(
    () => cookieConsent.services.length > 0,
    [cookieConsent.services.length],
  );

  const [modalToShow, setModalToShow] = useState(null);

  const contentRef = React.useRef();
  const [, setState] = useLocalStorage(modalToShow?.storageId);
  const track = useTracking();
  const trackPopup = (id, action) => {
    track({
      event: action,
      options: {
        popupID: id,
      },
    });
  };
  useEffect(() => {
    if (validModalsToShow && validModalsToShow?.length > 0) {
      setModalToShow(randomModal(validModalsToShow));
    }
  }, [validModalsToShow]);

  useEffect(() => {
    let timeoutId;
    if (hasCookieConsent && modalToShow) {
      timeoutId = setTimeout(() => {
        dialog.show();
        contentRef.current.focus();
      }, modalToShow?.timeout * 1000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasCookieConsent, modalToShow, hash]);

  if (open) {
    trackPopup(modalToShow?.storageId, 'displayed_popup');
  }

  const handleClose = (id, action) => {
    trackPopup(id, action);
    dialog.hide();
    setState(STATES.CLOSED);
  };

  if (!modalToShow) {
    return null;
  }

  return (
    <Modal
      autoFocus={false}
      contentClassName={styles.modalContent}
      header={modalToShow.title}
      onClickBackdrop={() => handleClose(modalToShow.storageId, 'closed_popup')}
      onClose={() => handleClose(modalToShow.storageId, 'closed_popup')}
      store={dialog}
      theme="secondary"
    >
      <div className={styles.container} ref={contentRef}>
        <div className={styles.col}>
          <Heading as="h1" size="h3">
            {modalToShow.heading}
          </Heading>
          <Content withParser>{modalToShow.content}</Content>
          {!!modalToShow.link && (
            <div className={styles.buttonWrapper}>
              <Button
                autoFocus={false}
                className={styles.button}
                isElevated
                onClick={() =>
                  handleClose(modalToShow.storageId, 'clicked_popup')
                }
                render={
                  <Link
                    rel="noopener noreferrer"
                    target={modalToShow.link.target || '_blank'}
                    to={modalToShow.link.url}
                  />
                }
              >
                {modalToShow.link.title}
              </Button>
            </div>
          )}
        </div>
        <div className={classNames(styles.col, styles.center)}>
          {!!modalToShow.image && (
            <Image
              alt={modalToShow.image.alt}
              className={styles.image}
              src={modalToShow.image.src}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

export default TimedModal;
